import { DOCUMENT } from '@angular/common';
import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';

// Bootstrap 5 grid min-widths (px). Single source for viewport-driven TS so it
// stays in lockstep with the SCSS media queries.
export const BOOTSTRAP_BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
} as const;

// Bootstrap-aligned device buckets: mobile < md, tablet md..<lg, desktop >= lg.
export type DeviceBreakpoint = 'mobile' | 'tablet' | 'desktop';

@Injectable({ providedIn: 'root' })
export class BreakpointService {
  private readonly document: Document = inject(DOCUMENT);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly current: WritableSignal<DeviceBreakpoint> = signal<DeviceBreakpoint>('desktop');

  public readonly breakpoint: Signal<DeviceBreakpoint> = this.current.asReadonly();
  public readonly isMobile: Signal<boolean> = computed((): boolean => this.current() === 'mobile');
  public readonly isTablet: Signal<boolean> = computed((): boolean => this.current() === 'tablet');
  public readonly isDesktop: Signal<boolean> = computed(
    (): boolean => this.current() === 'desktop',
  );

  public constructor() {
    const view: (Window & typeof globalThis) | null = this.document.defaultView;
    if (!view?.matchMedia) {
      // SSR / jsdom without matchMedia: keep the desktop default (full labels).
      return;
    }

    const mdUp: MediaQueryList = view.matchMedia(`(min-width: ${BOOTSTRAP_BREAKPOINTS.md}px)`);
    const lgUp: MediaQueryList = view.matchMedia(`(min-width: ${BOOTSTRAP_BREAKPOINTS.lg}px)`);

    const update = (): void => this.current.set(this.bucketFor(mdUp.matches, lgUp.matches));
    update();

    mdUp.addEventListener('change', update);
    lgUp.addEventListener('change', update);
    this.destroyRef.onDestroy((): void => {
      mdUp.removeEventListener('change', update);
      lgUp.removeEventListener('change', update);
    });
  }

  private bucketFor(mdUp: boolean, lgUp: boolean): DeviceBreakpoint {
    if (lgUp) return 'desktop';
    if (mdUp) return 'tablet';
    return 'mobile';
  }
}
